import React, { useEffect, useRef, useState } from 'react';
import { PickList } from 'primereact/picklist';
import { Toast } from 'primereact/toast';
import { InputNumber } from 'primereact/inputnumber';
import { Menu } from 'primereact/menu';

import ApiService from '../services/ApiService';
import { useAuth } from '../context/auth';

import './MachineRecipes.css';
import { Button } from 'primereact/button';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

// Import new components
import CategoryFilter from './components/CategoryFilter';
import IngredientsOverview from './components/IngredientsOverview';
import ActionsBar from './components/ActionsBar';

const api = new ApiService();

const MachineRecipesTest = (props) => {
	console.log('props', props);
	const { machineId } = props.location.state ? props.location.state : '';
	const [machinePrice, setMachinePrice] = useState(props.location.state ? props.location.state.pricing.original : 0);
	const { currency } = props.location.state ? props.location.state.pricing : '';
	const [soupOffsetPrice, setSoupOffsetPrice] = useState(0);
	const toast = useRef(null);
	const history = useHistory();

	// State for context menus and UI interactions per recipe
	const [recipeMenuRefs, setRecipeMenuRefs] = useState({});
	const [showPriceInputs, setShowPriceInputs] = useState({});
	const [showBreakdowns, setShowBreakdowns] = useState({});

	const [machineRecipesId, setMachineRecipesId] = useState([]);
	const [machineRecipes, setMachineRecipes] = useState([]);
	const [allRecipes, setAllRecipes] = useState([]);
	const [recipesNotInMachine, setRecipesNotInMachine] = useState([]);
	const [showButton, setShowButton] = useState(false);
	const [ingredientFilterActive, setIngredientFilterActive] = useState(false);
	const [selectedIngredients, setSelectedIngredients] = useState([]);
	const [ingredientMatchMode, setIngredientMatchMode] = useState('any'); // 'any' or 'all'
	const [doneLoading, setDoneLoading] = useState(false);

	const [allIncludingInMachine, setAllIncludingInMachine] = useState([]);
	const [machineIngredients, setMachineIngredients] = useState([]);
	const [selectedCategory, setSelectedCategory] = useState('');
	const [availableCategories, setAvailableCategories] = useState([]);
	const [categoryFilterMode, setCategoryFilterMode] = useState('include'); // 'include' or 'exclude'
	const [hiddenMachineRecipes, setHiddenMachineRecipes] = useState([]);
	const [isFilterExpanded, setIsFilterExpanded] = useState(false);

	const { authTokens } = useAuth();

	const [resetValuesMachineRecipes, setResetValuesMachineRecipes] = useState([]);
	const [resetValuesRecipesNotInMachine, setResetValuesRecipesNotInMachine] = useState([]);

	const headers = {
		'X-Firebase-Auth-Client': authTokens.token,
	};

	// Fetch machine details to get soupOffsetPrice
	const fetchMachineDetails = async () => {
		try {
			const response = await api.getMachine(machineId, headers);
			if (response && response.data) {
				setSoupOffsetPrice(response.data.soupOffsetPrice || 0);
			}
		} catch (error) {
			console.error('Error fetching machine details:', error);
		}
	};

	useEffect(() => {
		if (machineId) {
			fetchMachineDetails();
		}
	}, []);

	useEffect(() => {
		fetchIngredients();
	}, []);

	const fetchIngredients = async () => {
		try {
			const allIngredients = await api.getAllIngredients(headers);
			setAllIncludingInMachine(allIngredients.data);
		} catch (error) {
			console.error(error);
		}
	};

	useEffect(() => {
		fetchIngredientsState();
	}, [allIncludingInMachine]);

	const fetchIngredientsState = async () => {
		if (allIncludingInMachine.length === 0) return;
		try {
			const arrayOfIngredientsState = await api.getStateIngredientsInMachine(machineId, headers);

			// this array looks like this
			/**
			 * [
			 * {available: true, enabled: true, compartment: 1, ingredientId: '23702cda-ec65-4f1c-9861-f2f5ee099db0'}
			 * {available: true, enabled: true, compartment: 2, ingredientId: '5414ae69-b4bf-4206-8ec5-f17b54a3b4ac'}
			 * ]
			 */

			// look at the ingredientId and find the ingredient in the all array
			// add the available, enabled, and compartment to the ingredient
			const ingredientsInMachine = arrayOfIngredientsState.data.map((state) => {
				const ingredient = allIncludingInMachine.find((ingredient) => ingredient.id === state.ingredientId);
				if (ingredient) {
					ingredient.available = state.available;
					ingredient.enabled = state.enabled;
					ingredient.compartment = state.compartment;
				}
				return ingredient;
			});

			// sort the ingredients by compartment
			ingredientsInMachine.sort((a, b) => {
				return a.compartment - b.compartment;
			});

			setMachineIngredients(ingredientsInMachine);
		} catch (error) {
			console.error(error);
		}
	};

	useEffect(() => {
		const headers = {
			'X-Firebase-Auth-Client': authTokens.token,
		};

		const getMachineRecipesByMachineId = async () => {
			const response = await api.getMachineRecipes(machineId, headers);
			setMachineRecipesId(response.data);
		};

		const getAllRecipes = async () => {
			const response = await api.getAllMachineRecipesWithoutPagination(headers);
			setAllRecipes(response.data.recipes);
		};

		getMachineRecipesByMachineId();
		getAllRecipes();
	}, [authTokens.token, machineId]);

	useEffect(() => {
		// Add useEffect to fetch categories
		const fetchCategories = async () => {
			try {
				const response = await api.getRecipeCategories(headers);
				setAvailableCategories(response.data);
			} catch (error) {
				console.error('Error fetching categories:', error);
			}
		};

		if (authTokens.token) {
			fetchCategories();
		}
	}, [authTokens.token]);

	// Auto-expand filter if there's an active category (better UX)
	useEffect(() => {
		if (selectedCategory && !isFilterExpanded) {
			setIsFilterExpanded(true);
		}
	}, [selectedCategory]);

	useEffect(() => {
		// find the recipe object for each recipe id and add it to the machineRecipes array and add the index
		const result = machineRecipesId.map((recipe) => {

			const recipeObj = allRecipes.find((r) => r.id === recipe.recipeId);
			const isSoup = recipeObj?.categories?.some(cat => cat.toUpperCase().includes('SOUP'));
			const basePrice = machinePrice + (isSoup ? soupOffsetPrice : 0);
			const priceOffset = recipe.priceOffset || 0;
			
			
			return {
				...recipeObj,
				index: recipe.index,
				priceOffset: priceOffset,
				totalPrice: priceOffset + basePrice,
			};
		});

		// sort the machineRecipes array by index
		result.sort((a, b) => a.index - b.index);

		// if recipe does not contain smoothieParts than filter it out
		let recipesWithSmoothieParts = result.filter((recipe) => recipe.smoothieParts);

		// Add filtering flags to each recipe instead of filtering them out
		recipesWithSmoothieParts = recipesWithSmoothieParts.map((recipe) => {
			let isFilteredOut = false;
			let isIngredientFiltered = false;

			// Category filtering
			if (selectedCategory) {
				const hasCategory = recipe.categories && recipe.categories.includes(selectedCategory);
				const shouldShow = categoryFilterMode === 'include' ? hasCategory : !hasCategory;
				isFilteredOut = !shouldShow;
			}

			// Ingredient filtering
			if (selectedIngredients.length > 0) {
				const recipeIngredients = recipe.smoothieParts?.map(part => part.flavor.name) || [];
				const hasMatchingIngredients = ingredientMatchMode === 'any' 
					? selectedIngredients.some(ingredient => recipeIngredients.includes(ingredient))
					: selectedIngredients.every(ingredient => recipeIngredients.includes(ingredient));
				isIngredientFiltered = !hasMatchingIngredients;
			}
			
			return {
				...recipe,
				isFilteredOut,
				isIngredientFiltered
			};
		});

		// Set hidden recipes for category filter warning
		setHiddenMachineRecipes(recipesWithSmoothieParts.filter(recipe => recipe.isFilteredOut));

		setMachineRecipes(recipesWithSmoothieParts);
		setResetValuesMachineRecipes(recipesWithSmoothieParts);

		// find the recipes that are not in the machine
		let recipesNotInMachine = allRecipes.filter((recipe) => {
			return !machineRecipesId.some((machineRecipe) => machineRecipe.recipeId === recipe.id);
		});

		// Apply category filter if selected
		if (selectedCategory) {
			recipesNotInMachine = recipesNotInMachine.filter((recipe) => {
				const hasCategory = recipe.categories && recipe.categories.includes(selectedCategory);
				return categoryFilterMode === 'include' ? hasCategory : !hasCategory;
			});
		}

		// Apply ingredient filtering to recipesNotInMachine
		if (selectedIngredients.length > 0) {
			recipesNotInMachine = recipesNotInMachine.filter((recipe) => {
				if (!recipe.smoothieParts) return false;
				
				const recipeIngredients = recipe.smoothieParts.map((smoothiePart) => smoothiePart.flavor.name);
				const hasMatchingIngredients = ingredientMatchMode === 'any' 
					? selectedIngredients.some(ingredient => recipeIngredients.includes(ingredient))
					: selectedIngredients.every(ingredient => recipeIngredients.includes(ingredient));
				
				return hasMatchingIngredients;
			});
		}

		// Always ensure recipes can be made by the machine (all recipe ingredients must be available)
		if (machineIngredients.length > 0) {
			// return only recipes that contain the ingredients in the machine
			const recipesWithIngredients = recipesNotInMachine.filter((recipe) => {
				if (!recipe.smoothieParts) return false;
				
				const recipeIngredientsName = recipe.smoothieParts.map((smoothiePart) => smoothiePart.flavor.name);
				const machineIngredientsName = machineIngredients.map((ingredient) => ingredient.flavor.name);

				// check if all ingredients in the recipe array are in the machine ingredients array
				return recipeIngredientsName.every((ingredient) => machineIngredientsName.includes(ingredient));
			});

			setRecipesNotInMachine(recipesWithIngredients);
			setResetValuesRecipesNotInMachine(recipesWithIngredients);
		} else {
			setRecipesNotInMachine(recipesNotInMachine);
			setResetValuesRecipesNotInMachine(recipesNotInMachine);
		}
	}, [machineRecipesId, allRecipes, machineIngredients, selectedCategory, categoryFilterMode, selectedIngredients, ingredientMatchMode, machinePrice, soupOffsetPrice]);

	useEffect(() => {
		if (machineRecipes.length > 0 && !machineRecipes[0].smoothieParts) {
			return;
		}
		// console.log('machineRecipes', machineRecipes);

		// check if ingredinents in machine are the same as the ingredients in the result array
		const ingredientIsNotInMachineAnymore = machineRecipes.map((recipe) => {
			// console.log('recipe', recipe);
			const recipeIngredientsName = recipe.smoothieParts.map((smoothiePart) => smoothiePart.flavor.name);
			const machineIngredientsName = machineIngredients.map((ingredient) => ingredient.flavor.name);

			// check if all ingredients in the recipe array are in the machine ingredients array but not in the same order and not all of the machine ingredients are in the recipe array, just look for the ones that are
			return (
				recipeIngredientsName.every((ingredient) => machineIngredientsName.includes(ingredient)) &&
				machineIngredientsName.some((ingredient) => recipeIngredientsName.includes(ingredient))
			);
		});

		// add a flag to the recipe object if the ingredients are not in the machine anymore
		machineRecipes.forEach((recipe, index) => {
			let newKey = 'valid';
			recipe[newKey] = ingredientIsNotInMachineAnymore[index];
		});

		setMachineRecipes(machineRecipes);
	}, [machineRecipes, machineIngredients, doneLoading]);

	useEffect(() => {
		if (machineRecipes.length > 0 && !machineRecipes[0].smoothieParts) {
			return;
		}

		// get all ingredinets from all recipes in the machine
		const allIngredients = machineRecipes.map((recipe) => recipe.smoothieParts.map((item) => item.flavor.name)).flat();

		// remove duplicates
		const uniqueIngredients = [...new Set(allIngredients)];

		// look at ingredients in machine and remove the ones that are in the machine
		const unusedIngredients = machineIngredients.filter(
			(ingredient) => !uniqueIngredients.includes(ingredient.flavor.name),
		);

		// highlight the ingredient chips that are not in the machine
		const chips = document.querySelectorAll('.overview-ingredient-chip');

		chips.forEach((chip) => {
			const chipText = chip.querySelector('.overview-ingredient-label').textContent;
			if (unusedIngredients.map((ingredient) => ingredient.flavor.name).includes(chipText)) {
				chip.classList.add('overview-ingredient--unused');
			} else {
				chip.classList.remove('overview-ingredient--unused');
			}
		});
	}, [machineRecipes, machineIngredients]);

	useEffect(() => {
		// if machineRecipes fully loaded and has the valid key, set loading to false
		if (machineRecipes.length > 0) {
			// when every recipe has the valid key, set loading to false
			const loading = machineRecipes.every((recipe) => recipe.valid !== undefined);
			console.log('loading', loading);
			if (loading) {
				setDoneLoading(true);
			}
		}
	}, [machineRecipes, machineIngredients]);

	const moveFromMachineToNotInMachine = (event) => {
		// console.log('from in to not ', event);
		// move to not in machine array
		const movedRecipe = event.value[0];

		// remove index from moved recipe
		const movedRecipeWithoutIndex = {
			...movedRecipe,
			index: null,
		};

		// add moved recipe to not in machine array
		setRecipesNotInMachine([...recipesNotInMachine, movedRecipeWithoutIndex]);

		// remove moved recipe from machine array
		const newMachineRecipes = machineRecipes.filter((recipe) => recipe.id !== movedRecipe.id);
		setMachineRecipes(newMachineRecipes);

		// update index of recipes in machine
		const updatedMachineRecipes = newMachineRecipes.map((recipe, index) => {
			return {
				...recipe,
				index: index,
			};
		});

		setMachineRecipes(updatedMachineRecipes);
		// console.log(updatedMachineRecipes);
	};

	const moveFromNotInMachineToMachine = (event) => {
		// console.log('from not to in ', event);
		// move to machine array
		const movedRecipe = event.value[0];

		// add index to moved recipe
		const movedRecipeWithIndex = {
			...movedRecipe,
			index: machineRecipes.length,
		};

		// add moved recipe to machine array
		setMachineRecipes([...machineRecipes, movedRecipeWithIndex]);

		// remove moved recipe from not in machine array
		const newRecipesNotInMachine = recipesNotInMachine.filter((recipe) => recipe.id !== movedRecipe.id);
		setRecipesNotInMachine(newRecipesNotInMachine);

		// console.log(machineRecipes);
	};

	const moveInsideMachine = (event) => {
		setShowButton(true);
		const source = event.source;
		const target = event.target;

		setMachineRecipes(target);
		setRecipesNotInMachine(source);
	};

	const removeAllRecipesFromMachine = () => {
		setRecipesNotInMachine([...recipesNotInMachine, ...machineRecipes]);
		setMachineRecipes([]);
	};

	const addAllRecipesToMachine = () => {
		// console.log('addAllRecipesToMachine');
		setMachineRecipes([...machineRecipes, ...recipesNotInMachine]);
		setRecipesNotInMachine([]);
	};

	// Helper to check if there are invalid prices
	const hasInvalidPrices = () => {
		return machineRecipes.some(recipe => {
			const validation = validatePriceOffset(recipe.priceOffset || 0, recipe.categories || []);
			return !validation.isValid;
		});
	};

	const save = () => {
		// Check if all prices are valid before saving
		if (hasInvalidPrices()) {
			toast.current.show({
				severity: 'error',
				summary: 'Cannot Save',
				detail: 'Please fix invalid price offsets before saving',
				life: 4000,
			});
			return;
		}

		setShowButton(false);
		const machineRecipesToSave = machineRecipes.map((recipe, index) => {
			return {
				index: index,
				recipeId: recipe.id,
				priceOffset: recipe.priceOffset || 0
			};
		});

		const saveMachineRecipes = async () => {
			const response = await api.updateMachineRecipes(machineId, machineRecipesToSave, headers);

			if (response.status !== 200) {
				toast.current.show({
					severity: 'error',
					summary: 'Error',
					detail: 'Machine Recipes have not been saved',
					life: 3000,
				});
				return;
			} else {
				toast.current.show({
					severity: 'success',
					summary: 'Success',
					detail: 'Machine Recipes have been saved',
					life: 3000,
				});
			}
		};

		saveMachineRecipes();
	};

	const handleHighlightChips = (event) => {
		if (event.value.length === 0) {
			const chips = document.querySelectorAll('.overview-ingredient-chip');
			chips.forEach((chip) => {
				chip.classList.remove('overview-ingredient--highlighted');
			});
			return;
		}

		const value = event.value;

		// Get ingredients from selected recipes
		const ingredients = value[0].smoothieParts?.map((smoothiePart) => smoothiePart.flavor.name) || [];

		const chips = document.querySelectorAll('.overview-ingredient-chip');

		chips.forEach((chip) => {
			const chipText = chip.querySelector('.overview-ingredient-label').textContent;
			if (ingredients.includes(chipText)) {
				chip.classList.add('overview-ingredient--highlighted');
			} else {
				chip.classList.remove('overview-ingredient--highlighted');
			}
		});
	};

	const handleIngredientToggle = (ingredient) => {
		const isSelected = selectedIngredients.includes(ingredient);
		
		if (isSelected) {
			// Remove ingredient
			setSelectedIngredients(selectedIngredients.filter(item => item !== ingredient));
		} else {
			// Add ingredient
			setSelectedIngredients([...selectedIngredients, ingredient]);
		}

		setIngredientFilterActive(true);
	};

	const handleIngredientFilterClear = () => {
		setSelectedIngredients([]);
		setIngredientFilterActive(false);
	};

	const handleIngredientMatchModeChange = (mode) => {
		setIngredientMatchMode(mode);
	};

	const handleCategoryFilterChange = (e) => {
		setSelectedCategory(e.value);
	};

	const handleCategoryFilterModeChange = (e) => {
		setCategoryFilterMode(e.value);
	};

	const clearCategoryFilter = () => {
		setSelectedCategory('');
		setCategoryFilterMode('include');
	};

	const toggleFilterExpanded = () => {
		setIsFilterExpanded(!isFilterExpanded);
	};

	const handleCategoryFilterChangeAndClose = (e) => {
		handleCategoryFilterChange(e);
		// Auto-collapse on mobile after selection
		if (window.innerWidth <= 768) {
			setIsFilterExpanded(false);
		}
	};

	const editRecipe = async (recipe) => {
		history.push('/recipe/update', recipe);
	};

	const viewRecipeAmounts = () => {
		history.push(`/machines/${machineId}/recipe-amounts`);
	};

	// Helper functions for UI state management
	const getMenuRef = (recipeId) => {
		if (!recipeMenuRefs[recipeId]) {
			setRecipeMenuRefs(prev => ({
				...prev,
				[recipeId]: React.createRef()
			}));
		}
		return recipeMenuRefs[recipeId];
	};

	const togglePriceInput = (recipeId) => {
		setShowPriceInputs(prev => ({
			...prev,
			[recipeId]: !prev[recipeId]
		}));
	};

	const toggleBreakdown = (recipeId) => {
		setShowBreakdowns(prev => ({
			...prev,
			[recipeId]: !prev[recipeId]
		}));
	};

	const handlePriceOffsetChange = (recipeId, newOffset) => {
		// Find the recipe to validate
		const recipe = machineRecipes.find(r => r.id === recipeId);
		if (!recipe) return;
		
		// ALWAYS update the UI with user input (so validation can work)
		const updatedRecipes = machineRecipes.map((r) => {
			if (r.id === recipeId) {
				// Check if recipe is soup to apply soupOffsetPrice
				const isSoup = r.categories?.some(cat => cat.toUpperCase().includes('SOUP'));
				const basePrice = machinePrice + (isSoup ? soupOffsetPrice : 0);
				
				// Always store the user input and calculate total (even if invalid)
				const updatedRecipe = {
					...r,
					priceOffset: newOffset || 0, // Always store user input
					totalPrice: (newOffset || 0) + basePrice
				};
				return updatedRecipe;
			}
			return r;
		});

		setMachineRecipes(updatedRecipes);
		setShowButton(true); // Show save button when prices change
	};

	const validatePriceOffset = (offset, recipeCategories = []) => {
		// Check if recipe is a soup (contains SOUP category)
		const isSoup = recipeCategories.some(cat => cat.toUpperCase().includes('SOUP'));
		const basePrice = machinePrice + (isSoup ? soupOffsetPrice : 0);
		const totalPrice = offset + basePrice;
		const minOffset = -basePrice; // Minimum to keep total price at 0
		
		// Total price cannot be negative
		if (totalPrice < 0) {
			return {
				isValid: false,
				message: `Totalprice cannot be negative. Minimum offset: ${minOffset.toFixed(2)} ${currency || 'EUR'}`,
				suggestedValue: minOffset,
				minOffset: minOffset
			};
		}
		
		return { isValid: true, message: '', suggestedValue: offset, minOffset: minOffset };
	};

	const handleReset = () => {
		// reset values to original (including price offsets)
		setShowButton(false);
		setMachineRecipes(resetValuesMachineRecipes);
		setRecipesNotInMachine(resetValuesRecipesNotInMachine);
		setSelectedCategory('');
		setCategoryFilterMode('include');
		setSelectedIngredients([]);
		setIngredientMatchMode('any');
		setIngredientFilterActive(false);
		toast.current.show({
			severity: 'warn',
			summary: 'Reset',
			detail: 'All filters, recipes and price offsets have been reset',
			life: 3000,
		});
	};

	// console.log('machineRecipes', machineRecipes);

	return (
		<div>
			<Toast ref={toast}></Toast>

			{/* Category Filter Component */}
			<CategoryFilter
				isExpanded={isFilterExpanded}
				selectedCategory={selectedCategory}
				categoryFilterMode={categoryFilterMode}
				availableCategories={availableCategories}
				hiddenMachineRecipes={hiddenMachineRecipes}
				onToggleExpanded={toggleFilterExpanded}
				onCategoryChange={handleCategoryFilterChangeAndClose}
				onFilterModeChange={handleCategoryFilterModeChange}
				onClearFilter={clearCategoryFilter}
			/>

			{/* Machine Ingredients Overview Component */}
			<IngredientsOverview
				machineIngredients={machineIngredients}
				selectedIngredients={selectedIngredients}
				ingredientMatchMode={ingredientMatchMode}
				onIngredientToggle={handleIngredientToggle}
				onMatchModeChange={handleIngredientMatchModeChange}
				onFilterClear={handleIngredientFilterClear}
			/>

			{/* Actions Bar Component */}
			<ActionsBar
				showSaveButton={showButton}
				onSave={save}
				onReset={handleReset}
				disabled={hasInvalidPrices()}
			/>

			<div className="machine-recipe-amounts-link">
				<Button label="Recipe ingredient amounts" icon="pi pi-table" outlined onClick={viewRecipeAmounts} />
			</div>


			<PickList
				source={recipesNotInMachine}
				target={machineRecipes}
				targetControls={
					<div className="flex flex-column gap-2 p-2">
						<Button 
							icon="pi pi-angle-double-left" 
							tooltip="Remove all recipes from machine"
							onClick={removeAllRecipesFromMachine}
							severity="secondary"
							text
							rounded
							size="small"
							className="p-2 hover:surface-100"
							style={{width: '2.5rem', height: '2.5rem'}}
						/>
					</div>
				}
				targetItemTemplate={(item) => {
					if (item.valid === undefined) {
						setDoneLoading(false);
					} else {
						setDoneLoading(true);
					}

					// Determine if item should be visually filtered
					const isFiltered = item.isFilteredOut;
					const isIngredientFiltered = item.isIngredientFiltered;

					return (
						<div className={`
							recipe-card relative px-1 py-2 border-round-md surface-50 border-1 surface-border
							transition-all transition-duration-200 hover:shadow-1 hover:surface-0 cursor-pointer
							${item.valid === false ? 'border-red-300 surface-red-50' : ''} 
							${isFiltered ? 'opacity-50' : ''} 
							${isIngredientFiltered ? 'opacity-60' : ''}
							${showPriceInputs[item.id] ? 'border-primary surface-primary-50' : ''}
						`}
						role="listitem"
						aria-label={`Recipe: ${item.name}${item.priceOffset ? `, Price offset: ${item.priceOffset}` : ''}`}>
							{/* Status Overlays */}
							{isFiltered && (
								<div className="absolute top-0 left-0 right-0 bottom-0 border-round-md flex align-items-center justify-content-center z-1" 
									 style={{backgroundColor: 'rgba(0,0,0,0.7)'}}>
									<div className="bg-bluegray-50 px-2 py-1 border-round-sm shadow-1">
										<span className="text-xs font-medium text-primary-900">
											<i className="pi pi-filter mr-1"></i>
											{categoryFilterMode === 'include' ? 'No' : 'Has'} {selectedCategory}
										</span>
									</div>
								</div>
							)}
							
							{isIngredientFiltered && !isFiltered && (
								<div className="absolute top-0 left-0 right-0 bottom-0 border-round-md flex align-items-center justify-content-center z-1" 
									 style={{backgroundColor: 'rgba(239,68,68,0.1)'}}>
									<div className="bg-red-500 text-white px-2 py-1 border-round-sm shadow-1">
										<span className="text-xs font-medium">
											<i className="pi pi-ban mr-1"></i>
											Ingredients
										</span>
									</div>
								</div>
							)}

							{/* Main Content - Horizontal Layout */}
							<div className="flex gap-2">
								{/* Right: Content */}
								<div className="flex-1 min-w-0">
									{/* Header with title and context menu */}
									<div className="flex justify-content-between align-items-start mb-2">
										<div className="flex align-items-center">
											<div className="w-3rem h-3rem border-round-sm overflow-hidden bg-gray-200 flex align-content-center justify-content-center mr-2">
												<img src={item.image} alt={item.name} className="w-full h-full object-fit-cover"/>
											</div>

											<div className="flex flex-column align-items-start">
												<h4 className="text-sm font-semibold text-900 line-height-3 m-0 mr-2">
													{item.name}
												</h4>

												{item.badgeIconUrl && (
													<div className="h-1rem flex align-items-center justify-content-center">
														<img src={item.badgeIconUrl} alt="label" className="w-full h-full object-fit-cover"/>
													</div>
												)}
											</div>
										</div>

										{(() => {
											const menuRef = getMenuRef(item.id);
											const showPriceInput = showPriceInputs[item.id] || false;
											const showBreakdown = showBreakdowns[item.id] || false;
											
											const menuItems = [
												{
													label: 'Edit Recipe',
													icon: 'pi pi-pencil',
													command: () => editRecipe(item)
												},
												{
													label: showPriceInput ? 'Hide Price' : 'Adjust Price', 
													icon: 'pi pi-euro',
													command: () => togglePriceInput(item.id)
												},
												{
													label: showBreakdown ? 'Hide Breakdown' : 'Price Breakdown',
													icon: 'pi pi-calculator',
													command: () => toggleBreakdown(item.id)
												}
											];
											
											return (
												<div className="relative flex-shrink-0">
													<Button
														icon="pi pi-ellipsis-h"
														size="small"
														text
														rounded
														severity="secondary"
														className="p-1 hover:surface-100"
														style={{width: '1.5rem', height: '1.5rem'}}
														aria-label="Options"
														onClick={(e) => menuRef?.current?.toggle(e)}
														disabled={isFiltered}
													/>
													<Menu 
														model={menuItems} 
														popup 
														ref={menuRef}
														className="text-sm"
													/>
													
												</div>
											);
										})()}
									</div>

									{/* Status Indicators */}
									{item.valid === false && (
										<div className="mb-2">
											<span className="bg-red-500 text-white px-2 py-1 border-round-sm text-xs font-medium">
												<i className="pi pi-exclamation-triangle mr-1"></i>
												Ingredients missing
											</span>
										</div>
									)}

									{/* Smoothie Parts - Full Names */}
									{item.smoothieParts && item.smoothieParts.length > 0 && (
										<div className="flex flex-wrap gap-1 mb-2">
											{item.smoothieParts.map((smoothiePart, index) => (
												<span key={index} className="bg-primary-50 text-primary-700 px-2 py-1 border-round-sm text-xs">
													{smoothiePart.flavor.name}
												</span>
											))}
										</div>
									)}

									{/* Clean Price Display */}
									{(() => {
										const isSoup = item.categories?.some(cat => cat.toUpperCase().includes('SOUP'));
										const offset = item.priceOffset || 0;
										const basePrice = machinePrice + (isSoup ? soupOffsetPrice : 0);
										const totalPrice = offset + basePrice;
										const showBreakdown = showBreakdowns[item.id] || false;
										const showPriceInput = showPriceInputs[item.id] || false;
										
										return (
											<div className="mt-1">
												{/* Inline Price Input Mode */}
												{showPriceInput ? (
													<div className="surface-100 px-2 py-2 border-round-sm border-primary">
														<div className="flex align-items-center gap-2 mb-2">
															<span className="text-xs font-medium text-primary">Adjust Price Offset:</span>
														</div>
														<div className="flex flex-column gap-2">
															<div className="flex align-items-center gap-2">
																<InputNumber
																	value={item.priceOffset || 0}
																	onValueChange={(e) => {
																		// Always allow user to type, validate on blur/enter
																		handlePriceOffsetChange(item.id, e.value);
																	}}
																	mode="currency"
																	currency={currency}
																	locale="de-DE"
																	minFractionDigits={2}
																	maxFractionDigits={2}
																	size="small"
																	className={`flex-1 ${!validatePriceOffset(item.priceOffset || 0, item.categories || []).isValid ? 'p-invalid' : ''}`}
																	style={{fontSize: '12px'}}
																	onKeyDown={(e) => {
																		if (e.key === 'Enter') {
																			const validation = validatePriceOffset(item.priceOffset || 0, item.categories || []);
																			if (validation.isValid) {
																				togglePriceInput(item.id);
																			} else {
																				// Focus stays on input - user must fix the error
																				e.preventDefault();
																			}
																		}
																		if (e.key === 'Escape') {
																			// Reset to original value on escape
																			const originalRecipe = resetValuesMachineRecipes.find(r => r.id === item.id);
																			if (originalRecipe) {
																				handlePriceOffsetChange(item.id, originalRecipe.priceOffset || 0);
																			}
																			togglePriceInput(item.id);
																		}
																	}}
																	placeholder="Enter offset..."
																/>
																<Button
																	icon="pi pi-check"
																	size="small"
																	severity="success"
																	className="p-1"
																	disabled={!validatePriceOffset(item.priceOffset || 0, item.categories || []).isValid}
																	onClick={() => {
																		const validation = validatePriceOffset(item.priceOffset || 0, item.categories || []);
																		if (validation.isValid) {
																			togglePriceInput(item.id);
																		}
																	}}
																	style={{width: '1.75rem', height: '1.75rem'}}
																	tooltip="Confirm (Enter)"
																/>
																<Button
																	icon="pi pi-times"
																	size="small"
																	severity="secondary"
																	text
																	className="p-1"
																	onClick={() => {
																		// Reset to original value before closing (same as Escape)
																		const originalRecipe = resetValuesMachineRecipes.find(r => r.id === item.id);
																		if (originalRecipe) {
																			handlePriceOffsetChange(item.id, originalRecipe.priceOffset || 0);
																		}
																		togglePriceInput(item.id);
																	}}
																	style={{width: '1.75rem', height: '1.75rem'}}
																	tooltip="Cancel (Escape)"
																/>
															</div>
															
															{/* Always visible range info and validation */}
															<div className="surface-200 px-2 py-1 border-round-sm text-xs">
																{(() => {
																	const validation = validatePriceOffset(item.priceOffset || 0, item.categories || []);
																	
																	if (!validation.isValid) {
																		return (
																			<div className="text-red-600 flex align-items-start gap-1">
																				<i className="pi pi-exclamation-triangle text-xs mt-1"></i>
																				<span>{validation.message}</span>
																			</div>
																		);
																	} else {
																		return (
																			<div className="text-600 flex align-items-center gap-1">
																				<i className="pi pi-info-circle text-xs"></i>
																				<span>Choose a valid range between: {validation.minOffset.toFixed(2)} {currency} and up</span>
																			</div>
																		);
																	}
																})()}
															</div>
														</div>
													</div>
												) : (
													// Normal Price Display
													<div className="surface-100 px-2 py-1 border-round-sm hover:surface-200 transition-all transition-duration-200">
														<div className="flex justify-content-between align-items-center">
															<div className="flex align-items-center gap-1">
																<span className="text-xs font-medium text-600">Total price:</span>
																{(offset !== 0 || (isSoup && soupOffsetPrice !== 0)) && (
																	<Button
																		icon={showBreakdown ? "pi pi-chevron-up" : "pi pi-chevron-down"}
																		size="small"
																		text
																		className="p-0 w-1rem h-1rem"
																		onClick={() => toggleBreakdown(item.id)}
																		style={{fontSize: '10px'}}
																	/>
																)}
															</div>
															<div className="flex align-items-center gap-1">
																{offset !== 0 && (
																	<span className={`text-xs ${offset < 0 ? 'text-red-600' : 'text-green-600'}`}>
																		{offset > 0 ? '+' : ''}{offset.toFixed(2)}
																	</span>
																)}
																{isSoup && soupOffsetPrice !== 0 && (
																	<i className="pi pi-tag text-orange-500 text-xs" title="Soup offset actief"></i>
																)}
																<span className="text-sm font-bold text-primary">
																	{new Intl.NumberFormat('nl-NL', { style: 'currency', currency: currency || 'EUR' }).format(totalPrice)}
																</span>
															</div>
														</div>
														
														{/* Expandable Breakdown */}
														{showBreakdown && (
															<div className="mt-2 pt-2 border-top-1 surface-border">
																<div className="flex flex-column gap-1">
																	<div className="flex justify-content-between text-xs">
																		<span className="text-600">Machine:</span>
																		<span>{new Intl.NumberFormat('nl-NL', { style: 'currency', currency: currency || 'EUR' }).format(machinePrice)}</span>
																	</div>
																	{isSoup && soupOffsetPrice !== 0 && (
																		<div className="flex justify-content-between text-xs">
																			<span className="text-600">Soup offset:</span>
																			<span className={soupOffsetPrice < 0 ? 'text-red-600' : 'text-green-600'}>
																				{soupOffsetPrice > 0 ? '+' : ''}{new Intl.NumberFormat('nl-NL', { style: 'currency', currency: currency || 'EUR' }).format(soupOffsetPrice)}
																			</span>
																		</div>
																	)}
																	{offset !== 0 && (
																		<div className="flex justify-content-between text-xs">
																			<span className="text-600">Recipe offset:</span>
																			<span className={offset < 0 ? 'text-red-600' : 'text-green-600'}>
																				{offset > 0 ? '+' : ''}{new Intl.NumberFormat('nl-NL', { style: 'currency', currency: currency || 'EUR' }).format(offset)}
																			</span>
																		</div>
																	)}
																</div>
															</div>
														)}
													</div>
												)}
											</div>
										);
									})()}
								</div>
							</div>
						</div>
					);
				}}
				sourceItemTemplate={(item) => {
					if (!item) {
						return null;
					}

					// Determine if item should be visually filtered
					const isFiltered = item.isFilteredOut;
					const isIngredientFiltered = item.isIngredientFiltered;

					return (
						<div className={`
							recipe-card relative px-1 py-2 border-round-md surface-50 border-1 surface-border
							transition-all transition-duration-200 hover:shadow-1 hover:surface-0 cursor-pointer
							${item.valid === false ? 'border-red-300 surface-red-50' : ''} 
							${isFiltered ? 'opacity-50' : ''} 
							${isIngredientFiltered ? 'opacity-60' : ''}
						`}
						role="listitem"
						aria-label={`Available recipe: ${item.name}`}>
							{/* Status Overlays */}
							{isFiltered && (
								<div className="absolute top-0 left-0 right-0 bottom-0 border-round-md flex align-items-center justify-content-center z-1" 
									 style={{backgroundColor: 'rgba(0,0,0,0.7)'}}>
									<div className="bg-bluegray-50 px-2 py-1 border-round-sm shadow-1">
										<span className="text-xs font-medium text-600">
											<i className="pi pi-filter mr-1"></i>
											{categoryFilterMode === 'include' ? 'No' : 'Has'} {selectedCategory}
										</span>
									</div>
								</div>
							)}
							
							{isIngredientFiltered && !isFiltered && (
								<div className="absolute top-0 left-0 right-0 bottom-0 border-round-md flex align-items-center justify-content-center z-1" 
									 style={{backgroundColor: 'rgba(239,68,68,0.1)'}}>
									<div className="bg-red-500 text-white px-2 py-1 border-round-sm shadow-1">
										<span className="text-xs font-medium">
											<i className="pi pi-ban mr-1"></i>
											Ingredients
										</span>
									</div>
								</div>
							)}

							{/* Main Content - Horizontal Layout */}
							<div className="flex gap-2">
								{/* Right: Content */}
								<div className="flex-1 min-w-0">
									{/* Header with title and context menu */}
									<div className="flex justify-content-between align-items-start mb-2">
										<div className="flex align-items-center">
											<div className="w-3rem h-3rem border-round-sm overflow-hidden bg-gray-200 flex align-content-center justify-content-center mr-2">
												<img src={item.image} alt={item.name} className="w-full h-full object-fit-cover"/>
											</div>

											<div className="flex flex-column align-items-start">
												<h4 className="text-sm font-semibold text-900 line-height-3 m-0 mr-2">
													{item.name}
												</h4>

												{item.badgeIconUrl && (
													<div className="h-1rem flex align-items-center justify-content-center">
														<img src={item.badgeIconUrl} alt="label" className="w-full h-full object-fit-cover"/>
													</div>
												)}
											</div>
										</div>
										{(() => {
											const menuRef = getMenuRef(`source-${item.id}`);
											
											const menuItems = [
												{
													label: 'Edit Recipe',
													icon: 'pi pi-pencil',
													command: () => editRecipe(item)
												}
											];
											
											return (
												<div className="flex-shrink-0">
													<Button
														icon="pi pi-ellipsis-h"
														size="small"
														text
														rounded
														severity="secondary"
														className="p-1 hover:surface-100"
														style={{width: '1.5rem', height: '1.5rem'}}
														aria-label="Options"
														onClick={(e) => menuRef?.current?.toggle(e)}
														disabled={isFiltered}
													/>
													<Menu 
														model={menuItems} 
														popup 
														ref={menuRef}
														className="text-sm"
													/>
												</div>
											);
										})()}
									</div>

									{/* Status Indicators */}
									{item.valid === false && (
										<div className="mb-2">
											<span className="bg-red-500 text-white px-2 py-1 border-round-sm text-xs font-medium">
												<i className="pi pi-exclamation-triangle mr-1"></i>
												Ingredients missing
											</span>
										</div>
									)}

									{/* Smoothie Parts - Full Names */}
									{item.smoothieParts && item.smoothieParts.length > 0 && (
										<div className="flex flex-wrap gap-1">
											{item.smoothieParts.map((smoothiePart, index) => (
												<span key={index} className="bg-primary-50 text-primary-700 px-2 py-1 border-round-sm text-xs">
													{smoothiePart.flavor.name}
												</span>
											))}
										</div>
									)}
								</div>
							</div>
						</div>
					);
				}}
				sourceHeader="All Recipes"
				targetHeader={`Recipes in Machine ${selectedCategory && hiddenMachineRecipes.length > 0 ? `(${hiddenMachineRecipes.length} dimmed)` : ''}`}
				showSourceControls={false}
				showTargetControls={true}
				onMoveToSource={moveFromMachineToNotInMachine}
				onMoveToTarget={moveFromNotInMachineToMachine}
				onChange={moveInsideMachine}
				sourceFilterPlaceholder="Search by name"
				targetFilterPlaceholder="Search by name"
				filter
				filterBy="name"
				onSourceSelectionChange={handleHighlightChips}
				onTargetSelectionChange={handleHighlightChips}
			/>
		</div>
	);
};

export default MachineRecipesTest;
